import { DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Table,
  message,
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import DatePicker from "../inputs/MomentDatePicker";
import { numberInputGuard } from "../inputs/numberInputGuard";
import GroupBadge, { isGroupRow } from "./GroupBadge";

/**
 * "Schedule" tab — the ordered plan of activities for a class.
 *
 * Montessori vocabulary (lesson / activity / milestone / event) rather than the senior
 * board's module / exam / assignment, and no score or weightage columns since
 * Montessori plans are not graded.
 */
function BoardSyllabus({ api, canEdit, viewOnly = false }) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const syllabus = useQuery(api.keys.syllabus, api.fetchSyllabus, {
    enabled: api.enabled,
  });

  // A group row is read-only on a branch board. In group scope every row is the
  // group's own, so it stays editable there.
  const rowEditable = (record) =>
    canEdit && (api.scope === "group" || !isGroupRow(record));

  // Whether the modal currently open is an editable one. Adding a new item is
  // always editable; opening an existing row follows that row.
  const editingRow = editId
    ? syllabus.data?.find((item) => item._id === editId)
    : null;
  const formEditable = editId ? rowEditable(editingRow) : canEdit;

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    form.resetFields();
  };

  const onError = (err) => {
    message.error(
      err?.response?.data?.error?.errors?.[0] || "Something went wrong",
    );
  };

  const invalidate = () => queryClient.invalidateQueries(api.keys.syllabus);

  const saveMutation = useMutation(
    (payload) =>
      editId ? api.updateSyllabus(editId, payload) : api.createSyllabus(payload),
    {
      onSuccess: () => {
        message.success(editId ? "Schedule item updated" : "Schedule item added");
        closeForm();
        invalidate();
      },
      onError,
    },
  );

  const deleteMutation = useMutation(api.deleteSyllabus, {
    onSuccess: () => {
      message.success("Schedule item deleted");
      invalidate();
    },
    onError,
  });

  // Populate the form when an existing row is opened for edit.
  useEffect(() => {
    if (!editId) return;
    const row = syllabus.data?.find((item) => item._id === editId);
    if (!row) return;
    form.setFieldsValue({
      order: row.order,
      name: row.name,
      type: row.type,
      start_date: row.start_date ? moment(row.start_date) : null,
      end_date: row.end_date ? moment(row.end_date) : null,
    });
  }, [editId, syllabus.data, form]);

  const onFinish = (values) => {
    saveMutation.mutate({
      order: Number(values.order),
      name: values.name,
      type: values.type,
      start_date: values.start_date
        ? values.start_date.format("YYYY-MM-DD")
        : null,
      end_date: values.end_date ? values.end_date.format("YYYY-MM-DD") : null,
    });
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await api.downloadSyllabus();
    } catch (err) {
      message.error("Could not download the schedule");
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      align: "center",
      width: 90,
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text, record) => (
        <span className="inline-flex items-center gap-2">
          <span className="capitalize">{text}</span>
          {api.scope === "class" && isGroupRow(record) && <GroupBadge compact />}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      align: "center",
      render: (text) => <span className="capitalize">{text}</span>,
    },
    {
      title: "Dates",
      align: "center",
      render: (record) => (
        <div className="flex flex-col gap-1">
          <div>
            {record?.start_date
              ? `Start: ${moment(record.start_date).format("DD/MM/YYYY")}`
              : "—"}
          </div>
          <div>
            {record?.end_date
              ? `End: ${moment(record.end_date).format("DD/MM/YYYY")}`
              : "—"}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <div className="flex items-center justify-center gap-3">
          <span
            className="text-blue-500 cursor-pointer"
            onClick={() => {
              setEditId(record._id);
              setShowForm(true);
            }}
          >
            {rowEditable(record) ? "Edit" : "View"}
          </span>
          {rowEditable(record) && (
            <Popconfirm
              title="Delete this schedule item?"
              onConfirm={() => deleteMutation.mutate(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                shape="circle"
                className="flex items-center justify-center"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="pt-8">
      <div className="bg-white p-6 rounded-xl">
        <div className="flex justify-between items-center pb-4">
          <div style={{ color: "#E46E31" }} className="text-2xl font-semibold">
            Schedule
          </div>
          <div className="flex gap-3">
            {/* The export is a branch-level PDF of one class's plan, so there
                is nothing to download from the group screen. */}
            {api.downloadSyllabus && !viewOnly && (
              <Button
                size="large"
                type="primary"
                onClick={handleDownload}
                loading={downloading}
                disabled={!syllabus.data?.length}
              >
                Download
              </Button>
            )}
            {canEdit && (
              <Button
                size="large"
                type="primary"
                onClick={() => {
                  setEditId(null);
                  form.resetFields();
                  setShowForm(true);
                }}
              >
                Add
              </Button>
            )}
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={syllabus.data ?? []}
          rowKey={(record) => record._id}
          loading={syllabus.isLoading}
          pagination={{ pageSize: 10, position: ["bottomCenter"] }}
        />
      </div>

      <Modal
        width={600}
        open={showForm}
        onCancel={closeForm}
        footer={null}
        destroyOnClose
      >
        <h3 className="text-lg font-bold pb-3">
          {!formEditable
            ? "Schedule Item"
            : editId
              ? "Update Schedule Item"
              : "Add Schedule Item"}
        </h3>
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="order"
            label="Order"
            rules={[{ required: true, message: "Please input the order!" }]}
          >
            <Input
              type="number"
              placeholder="Enter order"
              readOnly={!formEditable}
              {...numberInputGuard}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please input a name" }]}
          >
            <Input placeholder="Enter name" readOnly={!formEditable} />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Please select a type!" }]}
          >
            <Select
              placeholder="Select type"
              disabled={!formEditable}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              <Select.Option value="lesson">Lesson</Select.Option>
              <Select.Option value="activity">Activity</Select.Option>
              <Select.Option value="milestone">Milestone</Select.Option>
              <Select.Option value="event">Event</Select.Option>
            </Select>
          </Form.Item>

          <div style={{ display: "flex", gap: "8px" }}>
            <Form.Item name="start_date" label="Start Date" style={{ flex: 1 }}>
              <DatePicker
                className="w-full"
                format="DD-MM-YYYY"
                disabled={!formEditable}
                placeholder="Start Date"
                onChange={() => form.setFieldsValue({ end_date: null })}
              />
            </Form.Item>
            <Form.Item name="end_date" label="End Date" style={{ flex: 1 }}>
              <DatePicker
                className="w-full"
                format="DD-MM-YYYY"
                disabled={!formEditable}
                placeholder="End Date"
                disabledDate={(current) => {
                  const start = form.getFieldValue("start_date");
                  return current && start && current.isBefore(start, "day");
                }}
              />
            </Form.Item>
          </div>

          {formEditable && (
            <div className="flex justify-end items-center pt-4">
              <div className="inline-flex items-center space-x-2">
                <Button size="large" onClick={closeForm}>
                  Cancel
                </Button>
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={saveMutation.isLoading}
                >
                  {editId ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default BoardSyllabus;
